import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { userManager, isRedirectCallback, cleanCallbackUrl } from '../lib/oidcConfig';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const authApi = axios.create({
  baseURL: `${API}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Deux jetons cohabitent, volontairement :
 *
 *  - l'access token Keycloak, qui prouve *qui* est l'utilisateur. Il ne sert
 *    qu'une fois, à l'ouverture de session ;
 *  - le jeton de session opaque émis par l'API et conservé dans Redis, présenté
 *    à chaque appel ensuite.
 *
 * Le second n'est pas une redondance : il porte le rôle déjà résolu, il est
 * révocable côté serveur, et il évite de revalider un token Keycloak à chaque
 * requête.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cloudcost_token'));
  const [loading, setLoading] = useState(true);
  const initialised = useRef(false);

  useEffect(() => {
    // React monte deux fois les effets en développement ; sans ce garde-fou le
    // code d'autorisation serait consommé deux fois et le second échange
    // échouerait avec une erreur incompréhensible.
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      try {
        if (isRedirectCallback()) {
          const oidcUser = await userManager.signinRedirectCallback();
          // Nettoyer l'URL avant l'échange : si celui-ci échoue, un
          // rechargement ne doit pas rejouer un code déjà consommé.
          cleanCallbackUrl();
          const { data } = await authApi.post('/auth/oidc', {
            access_token: oidcUser.access_token,
          });
          localStorage.setItem('cloudcost_token', data.token);
          setToken(data.token);
          setUser(data.user);
          setLoading(false);
          return;
        }

        const stored = localStorage.getItem('cloudcost_token');
        if (stored) {
          const { data } = await authApi.get('/auth/me', {
            headers: { Authorization: `Bearer ${stored}` },
          });
          setToken(stored);
          setUser(data);
        }
      } catch (error) {
        console.error('[Auth] initialisation :', error);
        cleanCallbackUrl();
        localStorage.removeItem('cloudcost_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const login = async () => {
    // La page navigue vers Keycloak, qui redirige lui-même vers Microsoft.
    await userManager.signinRedirect();
  };

  const logout = async () => {
    try {
      const stored = localStorage.getItem('cloudcost_token');
      if (stored) {
        // Révoque le jeton côté serveur : il vit dans Redis, le retirer suffit
        // à invalider la session même si le navigateur garde une copie.
        await authApi.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${stored}` },
        });
      }
    } catch (error) {
      console.error('[Auth] révocation du jeton :', error);
    } finally {
      localStorage.removeItem('cloudcost_token');
      setToken(null);
      setUser(null);
      try {
        await userManager.signoutRedirect();
      } catch {
        window.location.href = '/';
      }
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      isAdmin,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
